import { FileModel, Page } from "../../../shared/models/models";
import type { ReadinessVM } from "../utils/midasReadiness";
import {
  CategoryReference,
  InstalledStockItem,
  InstalledStockItemsQuery, InternalStockItem,
  InternalStockItemsQuery
} from "../../inventory/models/models";

export interface CustomerMachineryState {
  query: MachinesQuery;
  machines: Record<string, Machine[]>;
  machine?: Machine;
  manufacturers: ManufacturerReference[];
  operationTypes: OperationType[];
  axisConfigurations: AxisConfiguration[];
  controlSystemBrands: ControlSystemBrand[];
  controlSystemModels: ControlSystemModel[];
  automationLevels: AutomationLevel[];
  types: MachineType[];
  loading: boolean;
  success?: string;
  error: any;
  // Decommission state
  decommissionedMachines: Machine[];
  decommissionedLoading: boolean;
  decommissionPreview: DecommissionPreview | null;
  decommissionPreviewLoading: boolean;
}

export interface CustomerMachineState {
  installedStockItemsQuery: InstalledStockItemsQuery;
  machine?: Machine;
  installedStockItems?: InstalledStockItem[];
  categories: CategoryReference[];
  manufacturers: ManufacturerReference[];
  loading: boolean;
  installedStockItemsLoading: boolean;
  success?: string;
  error: any;
}

export interface CustomerInternalStockState {
  internalStockItemsQuery: InternalStockItemsQuery;
  internalStockItems?: Page<InternalStockItem>;
  categories: CategoryReference[];
  manufacturers: ManufacturerReference[];
  loading: boolean;
  success?: string;
  error: any;
}

export interface TaperType {
  id: string;
  name: string;
  /** MACH-REF-I18N-1 translation key (kebab-case); absent/null = render `name` as-is. */
  code?: string | null;
  standard: string;
  description?: string;
  sortOrder?: number;
}

export interface MachineFileItem {
  id?: string;
  name: string;
  content: string; // base64 or blob URL
  displayOrder: number;
  isPrimary: boolean;
  size?: number;
}

export interface Machine {
  id: string;
  customerId: string;
  model: string;
  serialNumber: string;
  internalNumber: string;
  system: string;
  version: string;
  productionYear?: number;
  description: string;
  openServiceReportsCount: number;
  serviceStatusLabel?: string;
  serviceStatusColor?: string;
  serviceRequestStatus?: string;
  serviceRequestStatusColor?: string;
  activeServiceRequestCount?: number;
  statusDate: string;
  type: MachineType;
  manufacturer: ManufacturerReference;
  customBrandName: string;
  operationType: OperationType;
  axisConfiguration: AxisConfiguration;
  controlSystemBrand: ControlSystemBrand;
  numberOfStations?: number;
  automationLevel: AutomationLevel;
  catalogModelId?: string;
  catalogModel?: CatalogModelListItem;
  catalogImageUrl?: string;
  image: FileModel | null;
  files?: MachineFileItem[];
  botActivated: boolean;
  groupNames?: string[];
  isDecommissioned?: boolean;
  decommissionedDate?: string;
  decommissionReason?: string;
  // Phase 7F: TaperType + key specs. NULLABLE server-side (MachineViewModel.cs:47/48/50 = int?/decimal?/int?) and Program.cs:35 has no JsonOptions, so JSON carries an explicit null -> `| null`, not just `?`.
  taperType?: TaperType;
  maxSpindleRpm?: number | null;
  spindleHp?: number | null;
  spindleDirection?: string;
  toolCapacity?: number | null;
  tableSizeMm?: string;
  maxWorkpieceKg?: number;
  // LOC-1: Location fields (denormalized from LocationReference)
  locationId?: string;
  locationName?: string;
  parentLocationName?: string;
}

export interface PublicMachineProfile {
  id: string;
  model: string;
  customBrandName?: string;
  manufacturerName?: string;
  typeName?: string;
  serialNumber: string;
  productionYear?: number;
  operationTypeName?: string;
  controlSystemBrandName?: string;
  system?: string;
  axisConfigurationName?: string;
  automationLevelName?: string;
  // MACH-REF-I18N-1 reference codes beside the names (optional until the API sends them).
  typeCode?: string | null;
  operationTypeCode?: string | null;
  controlSystemBrandCode?: string | null;
  axisConfigurationCode?: string | null;
  automationLevelCode?: string | null;
  image?: FileModel;
  catalogModelId?: string;
  customerName?: string;
  customerId: string;
  groupNames: string[];
  isOwner: boolean;
}

export interface ChangeMachine {
  model: string;
  serialNumber: string;
  typeId?: string;
  manufacturerId?: string;
  customBrandName?: string;
  operationTypeId?: string;
  axisConfigurationId?: string;
  controlSystemBrandId?: string;
  automationLevelId?: string;
  system?: string;
  version?: string;
  productionYear?: number;
  description?: string;
  numberOfStations?: number;
  catalogModelId?: string;
  image: FileModel | null;
  files?: MachineFileItem[];
  locationId?: string;
}

export interface MachineType {
  id: string;
  name: string;
  /** MACH-REF-I18N-1 translation key (kebab-case); absent/null = render `name` as-is. */
  code?: string | null;
}

export interface OperationType {
  id: string;
  name: string;
  /** MACH-REF-I18N-1 translation key (kebab-case); absent/null = render `name` as-is. */
  code?: string | null;
}

export interface AxisConfiguration {
  id: string;
  name: string;
  /** MACH-REF-I18N-1 translation key (kebab-case); absent/null = render `name` as-is. */
  code?: string | null;
}

export interface ControlSystemBrand {
  id: string;
  name: string;
  /** MACH-REF-I18N-1 translation key (kebab-case); absent/null = render `name` as-is. */
  code?: string | null;
}

export interface ControlSystemModel {
  id: string;
  name: string;
}

export interface ManufacturerReference {
  id: string;
  name: string;
}

export interface AutomationLevel {
  id: string;
  name: string;
  /** MACH-REF-I18N-1 translation key (kebab-case); absent/null = render `name` as-is. */
  code?: string | null;
}

export interface MachinesQuery {
  search?: string;
  manufacturerId?: string;
  typeId?: string;
  groupBy?: string;
}

export const MachineTypes = {
    cnc: "CNC Machine",
    productionLine: "Production line",
    other: "Other",
};

// --- Catalog models ---

export interface CatalogManufacturerSummary {
  id: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  manufacturerId: string | null;
  parentManufacturerId: string | null;
  parentManufacturerName: string | null;
}

export interface CatalogModelListItem {
  id: string;
  techspexId: number;
  modelName: string;
  categoryCode: string;
  subType: string;
  cncBrand: string;
  numAxes: number | null;
  imageUrl: string | null;
  imageStandardUrl: string | null;
  imageDetailUrl: string | null;
  specsCount: number;
  manufacturer: CatalogManufacturerSummary;
  // Phase 7F: Classification data
  operationTypeName?: string;
  axisConfigurationName?: string;
  controlSystemBrandName?: string;
  taperTypeName?: string;
  // MACH-REF-I18N-1 reference codes beside the names (optional until the API sends them).
  operationTypeCode?: string | null;
  axisConfigurationCode?: string | null;
  controlSystemBrandCode?: string | null;
  taperTypeCode?: string | null;
  // Injected per page by the controller, NOT mapped from the entity:
  // CustomerCatalogController.cs:63-67 overwrites `vm.UnitsOnPlatform` for every
  // returned item from `GetUnitsOnPlatformCountsAsync`, defaulting to 0 on a
  // miss. It was missing here while the backend sent it, so both v1 read sites
  // cast (`CatalogModelCard.tsx:77`, `CatalogModelDetailPage.tsx:143`).
  unitsOnPlatform?: number;
  // Phase 7F: Key specs. NULLABLE server-side (CatalogModelListItemViewModel.cs:34/35/37 = int?/decimal?/int?), same as `numAxes` above -> `| null`. Typing these `?: number` is what let `!== undefined` guards compile and then throw at runtime (v2/catalog/catalogAtoms.tsx).
  maxSpindleRpm?: number | null;
  spindleHp?: number | null;
  spindleDirection?: string;
  toolCapacity?: number | null;
  tableSizeMm?: string;
  // Catalog model images (angle/detail photos)
  images?: CatalogModelImage[];
  // DR-1: OEM partnership (backend fills from OemPartners registry)
  isOemPartner?: boolean;
  oemPartnerName?: string | null;
  // DR-2: MIDAS data readiness (backend fills from CatalogModelReadiness; null = no data)
  readiness?: ReadinessVM | null;
}

export interface CatalogSpecItem {
  specKey: string;
  specValue: string;
  numericValue: number | null;
  unit: string;
}

export interface CatalogSpecSection {
  sectionName: string;
  specs: CatalogSpecItem[];
}

export interface CatalogModelImage {
  id: string;
  imageStandardUrl: string;
  imageDetailUrl: string;
  imageType: string;
  displayOrder: number;
  isPrimary: boolean;
}

export interface CatalogModelDetail {
  id: string;
  techspexId: number;
  modelName: string;
  categoryCode: string;
  subType: string;
  operationType: string;
  cncBrand: string;
  cncModel: string;
  numAxes: number | null;
  numAxesOpt: number | null;
  imageUrl: string | null;
  imagePath: string | null;
  imageStandardUrl: string | null;
  imageDetailUrl: string | null;
  sourceUrl: string;
  isActive: boolean;
  specsCount: number;
  /** Same injected field as on the list item — the DETAIL view model carries it
   *  too (CatalogModelDetailViewModel.cs:22, filled at
   *  CustomerCatalogController.cs:91). */
  unitsOnPlatform?: number;
  manufacturer: CatalogManufacturerSummary;
  specSections: CatalogSpecSection[];
  images?: CatalogModelImage[];
  // DR-3 (item 5): MIDAS readiness — same fields the list item carries, so the
  // detail page can render the readiness row + open the shared popup. Backend
  // fills these on the detail DTO in parallel; null/absent = no data.
  isOemPartner?: boolean;
  oemPartnerName?: string | null;
  readiness?: ReadinessVM | null;
}

export interface CatalogSearchResult {
  items: CatalogModelListItem[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface CatalogManufacturer {
  id: string;
  techspexId: number;
  name: string;
  slug: string;
  logoUrl: string | null;
  machineCount: number;
  manufacturerId: string | null;
  parentManufacturerId: string | null;
  parentManufacturerName: string | null;
  childManufacturers: CatalogManufacturerSummary[];
}

export interface CatalogCategory {
  id: number;
  code: string;
  name: string;
  modelCount: number;
}

export interface CatalogBrowseQuery {
  search?: string;
  categoryCode?: string;
  manufacturerId?: string;
  page: number;
  pageSize: number;
  // Phase 7F: Spec-based filters
  minRpm?: number;
  maxRpm?: number;
  minHp?: number;
  maxHp?: number;
  minTools?: number;
  maxTools?: number;
  taperTypeId?: string;
  controlSystemBrandId?: string;
  operationTypeId?: string;
  // DR-1: MIDAS Readiness filters
  oemReady?: boolean;
  // DR-2: MachineID integrated = readiness.dynamicCapability IN (proven, possible)
  machineIdIntegrated?: boolean;
}

export interface MachineCatalogState {
  // Dialog search state
  searchResults: CatalogModelListItem[];
  searchTotalCount: number;
  searchLoading: boolean;
  selectedModel: CatalogModelDetail | null;
  selectedModelLoading: boolean;
  manufacturers: CatalogManufacturer[];
  categories: CatalogCategory[];
  taperTypes: TaperType[];

  // Browse page state
  browseResults: CatalogModelListItem[];
  browseTotalCount: number;
  browseTotalPages: number;
  browseLoading: boolean;
}

// --- Decommission models ---

export interface ServiceRequestBrief {
  id: string;
  internalNumber: string;
  issue: string;
  statusCode: number;
}

export interface RfsSummary {
  draftCount: number;
  openNoOffersCount: number;
  negotiatingCount: number;
  inProgressCount: number;
  totalActiveCount: number;
  hasBlockingRequests: boolean;
  blockingRequests: ServiceRequestBrief[];
}

export interface DecommissionPreview {
  machineId: string;
  machineName: string;
  machineImage: string;
  rfsSummary: RfsSummary;
  canDecommission: boolean;
  blockReason: string;
}

// --- Report Problem models ---

export interface ReportProblemRequest {
  description: string;
  priorityCode?: string;
  photos?: string[];
}

export interface ReportProblemResponse {
  serviceRequestId: string;
  serviceReportNumber: string;
  message: string;
}